import { useEffect, useState } from 'react';
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import { EditOutlined, PlusOutlined, StopOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import {
  lookupsApi,
  type Lookup,
  type CreateLookupInput,
} from '../../../api/endpoints/lookups';
import { PageHeader } from '../../../components/common/PageHeader';

/** Etiquetas legibles para los catálogos conocidos. Si aparece uno nuevo, se muestra tal cual. */
const LIST_LABELS: Record<string, string> = {
  account_business1: 'Giros (Cuenta)',
  account_category: 'Categorías (Cuenta)',
  person_source: 'Fuentes (Contacto)',
  activity_type: 'Tipos de actividad',
  sale_stage: 'Etapas de venta',
  project_status: 'Estados de proyecto',
};

interface FormValues {
  value: string;
  label: string;
  sort: number;
  isDefault: boolean;
}

export function LookupsAdminPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [listName, setListName] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Lookup | null>(null);
  const [form] = Form.useForm<FormValues>();

  const namesQuery = useQuery({
    queryKey: ['lookups', 'names'],
    queryFn: () => lookupsApi.names(),
  });

  // Selecciona el primer catálogo cuando llegan los nombres.
  useEffect(() => {
    const first = namesQuery.data?.[0];
    if (!listName && first) {
      setListName(first);
    }
  }, [namesQuery.data, listName]);

  const { data, isLoading } = useQuery({
    queryKey: ['lookups', listName, 'admin'],
    queryFn: () => lookupsApi.byList(listName!, true),
    enabled: !!listName,
  });

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (editing) {
        return lookupsApi.update(editing.id, values);
      }
      const payload: CreateLookupInput = { ...values, listName: listName! };
      return lookupsApi.create(payload);
    },
    onSuccess: () => {
      message.success(editing ? 'Opción actualizada' : 'Opción creada');
      void queryClient.invalidateQueries({ queryKey: ['lookups'] });
      setModalOpen(false);
      setEditing(null);
      form.resetFields();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'No se pudo guardar';
      message.error(msg);
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => lookupsApi.remove(id),
    onSuccess: () => {
      message.success('Opción inactivada');
      void queryClient.invalidateQueries({ queryKey: ['lookups'] });
    },
    onError: () => message.error('No se pudo inactivar'),
  });

  const handleOpenCreate = (): void => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleOpenEdit = (row: Lookup): void => {
    setEditing(row);
    form.setFieldsValue({
      value: row.value,
      label: row.label,
      sort: row.sort,
      isDefault: row.isDefault,
    });
    setModalOpen(true);
  };

  const columns: ColumnsType<Lookup> = [
    {
      title: 'Etiqueta',
      dataIndex: 'label',
      render: (label: string, row) => (
        <Space>
          {label}
          {row.isDefault && <Tag color="blue">default</Tag>}
          {!row.active && <Tag>inactiva</Tag>}
        </Space>
      ),
    },
    { title: 'Valor', dataIndex: 'value', width: 140 },
    { title: 'Orden', dataIndex: 'sort', width: 90, align: 'center' },
    {
      title: '',
      key: 'actions',
      width: 130,
      align: 'right',
      render: (_, row) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            size="small"
            disabled={!row.editable}
            onClick={() => handleOpenEdit(row)}
          />
          <Popconfirm
            title="¿Inactivar opción?"
            description="Dejará de aparecer en los formularios. Los datos que ya la usan no se modifican."
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
            onConfirm={() => deactivateMutation.mutate(row.id)}
          >
            <Button icon={<StopOutlined />} size="small" danger disabled={!row.editable || !row.active} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const segmentedOptions = (namesQuery.data ?? []).map((n) => ({
    label: LIST_LABELS[n] ?? n,
    value: n,
  }));

  return (
    <>
      <PageHeader
        title="Catálogos"
        crumbs={[{ title: 'Admin' }, { title: 'Catálogos' }]}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleOpenCreate}
            disabled={!listName}
          >
            Nueva opción
          </Button>
        }
      />

      <Space style={{ marginBottom: 16 }}>
        <Segmented
          options={segmentedOptions}
          value={listName ?? undefined}
          onChange={(v) => setListName(v as string)}
        />
      </Space>

      <Table<Lookup>
        scroll={{ x: 'max-content' }}
        rowKey="id"
        loading={isLoading || namesQuery.isLoading}
        columns={columns}
        dataSource={data ?? []}
        pagination={false}
      />

      <Modal
        open={modalOpen}
        title={editing ? 'Editar opción' : 'Nueva opción'}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saveMutation.isPending}
        okText="Guardar"
        cancelText="Cancelar"
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={(values) => saveMutation.mutate(values)}
          initialValues={{ sort: 0, isDefault: false }}
        >
          <Form.Item
            label="Etiqueta (visible)"
            name="label"
            rules={[{ required: true, message: 'Obligatorio' }]}
          >
            <Input maxLength={120} />
          </Form.Item>
          <Form.Item
            label="Valor (código interno)"
            name="value"
            rules={[{ required: true, message: 'Obligatorio' }]}
            extra="Identificador corto, ej. TEC, CLI. No cambiar si ya hay datos usándolo."
          >
            <Input maxLength={40} disabled={!!editing} />
          </Form.Item>
          <Form.Item label="Orden" name="sort">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="¿Default?" name="isDefault" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
